import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, MbsSize, ModalService, TextEllipsisComponent } from 'mbs-ui-kit';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { Company } from 'mbs-ui-shared@models';
import { UtilityModalSelectEntityType } from 'mbs-ui-shared@modules/utility-selection-modal/const';
import { UtilitySelectionModalComponent } from 'mbs-ui-shared@modules/utility-selection-modal/utility-selection-modal.component';
import { EMPTY, from, map } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

// The names line that opens the shared company-selection modal — the storage wizard's companies step
// and the destination forms render company selection identically
@UntilDestroy()
@Component({
  selector: 'mbs-companies-picker',
  templateUrl: './companies-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, I18NextPipe, ReactiveFormsModule, TextEllipsisComponent]
})
export class CompaniesPickerComponent implements OnChanges {
  private modalService = inject(ModalService);
  private companiesFacade = inject(CompaniesFacade);
  private i18n = inject(I18NextPipe);
  private cdr = inject(ChangeDetectorRef);

  @Input() companiesControl: FormControl<Company[]>;
  // Preselected ids whose names are not known yet (e.g. loaded from the wizard session)
  @Input() selectedIds: string[] = [];
  @Input() disabledIds: string[] = [];
  @Input() disabledHint: string = null;
  @Input() disabled = false;
  // Only the destination context may end up with no companies at all
  @Input() allowEmptySelection = false;

  get selectedNames(): string {
    return (this.companiesControl?.value ?? []).map((company) => company.name).join(', ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedIds) this.resolvePreselected();
  }

  public openSelectionModal(): void {
    const currentIds = (this.companiesControl?.value ?? []).map((company) => company.id);
    // Until the user touches the control its value may still be empty while selectedIds carries the stored selection
    const selectedIds = this.companiesControl?.dirty || currentIds.length ? currentIds : (this.selectedIds ?? []);

    from(
      this.modalService.openCustom<Company[]>(UtilitySelectionModalComponent, {
        data: {
          model: selectedIds,
          modalTitle: this.i18n.transform('storages:addStorageAccountWizard.companiesStep.selectionModalTitle'),
          applyTo: UtilityModalSelectEntityType.companies,
          saveButtonText: this.i18n.transform('shared:buttons.save'),
          multipleSelect: true,
          disabledIds: this.disabledIds ?? [],
          disabledHint: this.disabledHint,
          showAddNewCompany: true,
          allowEmptySelection: this.allowEmptySelection
        },
        size: MbsSize.sm,
        collapsing: true
      })
    )
      .pipe(
        catchError(() => EMPTY),
        filter(Boolean),
        untilDestroyed(this)
      )
      .subscribe((companies) => {
        this.companiesControl.setValue(companies);
        this.companiesControl.markAsDirty();
        this.cdr.markForCheck();
      });
  }

  private resolvePreselected(): void {
    const known = (this.companiesControl?.value ?? []).map((company) => company.id);
    const missing = (this.selectedIds ?? []).filter((id) => !known.includes(id));

    if (!missing.length) return;

    this.companiesFacade
      .getCompanies({ ids: this.selectedIds })
      .pipe(
        map((response) => response.data ?? []),
        untilDestroyed(this)
      )
      .subscribe((companies) => {
        this.companiesControl.setValue(companies, { emitEvent: false });
        this.cdr.markForCheck();
      });
  }
}
